<template>
<tr class="dl-row">
    <td class="dl-cover" @click="selectProject">
        <img v-if="hasPoster" :src="project.posterUrl" :alt="project.title" width="36" height="36" @error="onPosterError" />
        <span v-else class="dl-generated" :style="{ backgroundColor: coverColor }">{{ coverInitials }}</span>
    </td>
    <td class="dl-pin">
        <button
            type="button"
            class="dl-pin-btn"
            :class="{ 'is-pinned': isPinned }"
            :aria-pressed="isPinned ? 'true' : 'false'"
            :aria-label="isPinned ? 'Unpin project' : 'Pin project'"
            @click.stop="togglePin"
        >
            <md-icon>push_pin</md-icon>
            <md-tooltip>{{ isPinned ? 'Unpin' : 'Pin' }}</md-tooltip>
        </button>
    </td>
    <td class="dl-name" @click="selectProject">
        <!-- Inner flex row rather than flexing the <td> itself: a table cell set
             to display:flex stops behaving as a table-cell and breaks column
             sizing. -->
        <div class="dl-name-inner">
            <span class="dl-name-text" :title="project.title">{{ project.title }}</span>
            <span v-if="project.unlocked" class="dl-showpass" title="Unlocked with a Show Pass">Show Pass</span>
        <span
            v-else-if="isLegacy"
            class="dl-legacy"
            :class="{ 'is-owner': isLegacyOwner }"
            :title="legacyTip"
            :aria-label="'Legacy project. ' + legacyTip"
            :role="isLegacyOwner ? 'button' : null"
            :tabindex="isLegacyOwner ? 0 : null"
            @click.stop="onLegacyBadge"
            @keydown.enter.stop.prevent="onLegacyBadge"
        >Legacy</span>
            <!-- Same marker as the grid tile, sized down and sitting inline after
                 the title. Kept in this flex row rather than its own column so
                 the table's column widths are untouched. -->
            <span
                v-if="hasActiveShowSync"
                class="dl-showsync"
                :class="showSyncStateClass"
                :title="showSyncTooltip"
                :aria-label="showSyncTooltip"
                role="img"
            >
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
                    <path fill="currentColor" d="M12 4V1L8 5l4 4V6a6 6 0 0 1 5.19 8.99l1.47 1.47A8 8 0 0 0 12 4zm0 14a6 6 0 0 1-5.19-8.99L5.34 7.54A8 8 0 0 0 12 20v3l4-4-4-4v3z"/>
                </svg>
            </span>
        </div>
    </td>
    <td class="dl-date" @click="selectProject">{{ lastChanged }}</td>
    <td class="dl-owner" @click="selectProject">{{ ownerLabel }}</td>
    <td class="dl-actions">
        <dashboard-kebab-menu
            ref="kebab"
            :project="project"
            @projectAccepted="$emit('projectAccepted', $event)"
            @projectDeleted="$emit('projectDeleted', $event)"
        />
    </td>
</tr>
</template>

<script>
/**
 * One row of the list view. A keyed component (like the grid tile) so it owns
 * its own poster-failed state and nav/cover logic — no shared maps that can
 * bleed across sections, which is what caused covers to vanish when pinning.
 */
import moment from 'moment';
import { mapGetters } from 'vuex';
import projectNav from '@/mixins/projectNav';
import projectCover from '@/mixins/projectCover';
import showSync from '@/mixins/showSync';
import DashboardKebabMenu from '@/components/dashboard/kebab-menu';

const DashboardListRow = {
    name: 'DashboardListRow',
    mixins: [projectNav, projectCover, showSync],
    props: ['project'],
    data() {
        return {
            ownerLabel: ''
        };
    },
    computed: {
        ...mapGetters(['user', 'ownerLabelByUID']),
        // Legacy tool (Josh, 5-6 Oct 2026): projects that still open in the legacy editor carry a "Legacy" badge in the
        // Show Pass spot (a Show Pass project is always current, so the two never meet). The owner clicks it to move the
        // project; anyone else just gets the tooltip.
        isLegacy() {
            return !!(this.project && this.project.isNew !== true && !this.project.isSample);
        },
        isLegacyOwner() {
            return this.isLegacy && !!this.user && this.user.uid === this.project.ownerUID;
        },
        legacyTip() {
            return this.isLegacyOwner ? 'Move to the current editor\u2026' : 'Opens in the legacy editor';
        },
        lastChanged() {
            const d = this.project.lastChangedDate ? moment(this.project.lastChangedDate) : moment();
            return d.format('MMM D, YYYY');
        },
        isPinned() {
            const up = this.user && this.user.projects && this.user.projects[this.project.id];
            return !!(up && up.pinned);
        }
    },
    watch: {
        'project.ownerUID': {
            immediate: true,
            handler() {
                this.resolveOwner();
            }
        }
    },
    methods: {
        onLegacyBadge() {
            if (this.isLegacyOwner && this.$refs.kebab) this.$refs.kebab.openMove();
        },
        togglePin() {
            this.$store.dispatch('togglePinProject', {
                projectId: this.project.id,
                pinned: !this.isPinned
            }).catch((e) => {
                console.error('Error toggling pin:', e);
                if (this.$toast) this.$toast.error('Could not update pin. Please try again.');
            });
        },
        async resolveOwner() {
            try {
                this.ownerLabel = await this.ownerLabelByUID(this.project.ownerUID);
            } catch (e) {
                this.ownerLabel = '';
            }
        }
    },
    components: {
        'dashboard-kebab-menu': DashboardKebabMenu
    }
};
export default DashboardListRow;
</script>

<style lang="scss">
.dl-row .dl-name .dl-showsync {
    // Same accent tokens as the grid tile's marker, so light/dark behaviour is
    // identical in both views. flex:0 0 auto for the same reason as the Show
    // Pass badge below — it must not stretch or shrink in the flex row.
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;

    &.is-active {
        color: var(--accent-ink, #12718a);
    }
    &.is-failed {
        color: var(--danger, #c0392b);
    }
}
.dl-row .dl-name .dl-legacy {
    flex: 0 0 auto;
    background: #fff;
    color: #12718a;
    border: 1px solid #12718a;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    padding: 1px 7px;
    border-radius: 10px;
    white-space: nowrap;
    cursor: default;
    &.is-owner { cursor: pointer; }
    &.is-owner:hover, &.is-owner:focus-visible { background: #e6f4f6; }
}
.md-theme-show-mode .dl-row .dl-name .dl-legacy { background: #263238; color: #73d5e3; border-color: #73d5e3; }
.dl-row .dl-name .dl-showpass {
    // Spacing comes from the flex gap on .dl-name-inner now; a margin here
    // would reappear as an indent if the badge ever wrapped.
    flex: 0 0 auto;
    background: #0d2744;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 10px;
    white-space: nowrap;
}
</style>


// WEBPACK FOOTER //
// dashboard-list-row.vue?0366a498